import React, { useMemo, useRef } from "react";
import Plot from "../../graphViewV2/charts/PlotlyChart";
import { buildWqDualAxisTicks } from "../../graphViewV2/charts/axisTicks";
import { plotlyAxisBase, plotlyAxisTitle } from "../../graphViewV2/charts/plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "../../graphViewV2/charts/plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  POND_STAT_DESKTOP_HEIGHT,
  POND_STAT_MOBILE_HEIGHT,
  useGraphV2PlotHeight,
} from "../../graphViewV2/ChartFullscreenShell";
import { formatDisplayDate } from "../../../../utils/formatDisplayDate";

function pickEvenlySpacedIndices(n, maxTicks) {
  if (n <= 0) return [];
  if (n <= maxTicks) return Array.from({ length: n }, (_, i) => i);
  const idx = new Set([0, n - 1]);
  const inner = maxTicks - idx.size;
  for (let k = 1; k <= inner; k++) {
    idx.add(Math.round((k * (n - 1)) / (inner + 1)));
  }
  return [...idx].sort((a, b) => a - b);
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {boolean} props.isMobile
 * @param {Array<{ dateLabel: string, dateMs: number, [key: string]: any }>} props.rows
 * @param {Array<{ key: string, label: string, color?: string }>} props.seriesDefs
 * @param {{ primaryKeys: string[], secondaryKeys: string[], secondaryRange: [number, number], primaryTitle?: string, secondaryTitle?: string }} [props.dualAxis]
 * @param {string} [props.yAxisTitle] — single-axis Y label (default mg/L for WQ charts)
 */
export default function PondStatTimeSeriesChart({
  title,
  isMobile,
  rows,
  seriesDefs,
  dualAxis,
  yAxisTitle = "mg/L",
}) {
  const plotRef = useRef(null);

  const { categoryarray, msToCategoryLabel } = useMemo(() => {
    const msToLabel = new Map();
    (rows || []).forEach((r) => {
      const ms = r.dateMs;
      if (ms == null || Number.isNaN(ms)) return;
      const lab = String(r.dateLabel ?? "").trim();
      if (!msToLabel.has(ms)) {
        msToLabel.set(ms, lab || formatDisplayDate(ms));
      }
    });
    const sorted = [...msToLabel.keys()].sort((a, b) => a - b);
    const categoryarray0 = sorted.map((ms) => msToLabel.get(ms));
    const msToCategoryLabel0 = new Map(sorted.map((ms) => [ms, msToLabel.get(ms)]));
    return { categoryarray: categoryarray0, msToCategoryLabel: msToCategoryLabel0 };
  }, [rows]);

  const xAxisTickLayout = useMemo(() => {
    const n = categoryarray.length;
    if (n === 0) {
      return {
        tickvals: null,
        ticktext: null,
        tickangle: 0,
        marginBottom: isMobile ? 100 : 90,
        tickFontSize: isMobile ? 14 : 16,
      };
    }
    const labelCap = isMobile ? 10 : 16;
    const ix = pickEvenlySpacedIndices(n, labelCap);
    const displayLabels = ix.map((i) => categoryarray[i]);
    const marginBottom = isMobile
      ? Math.min(200, 100 + Math.min(displayLabels.length * 3, 72))
      : Math.min(200, 96 + Math.min(displayLabels.length * 3, 64));
    const tickFontSize =
      n > 14 ? (isMobile ? 9 : 10) : n > 9 ? (isMobile ? 11 : 12) : isMobile ? 14 : 16;
    return {
      tickvals: displayLabels,
      ticktext: displayLabels,
      tickangle: 0,
      marginBottom,
      tickFontSize,
    };
  }, [categoryarray, isMobile]);

  const secondaryKeySet = useMemo(
    () => new Set(dualAxis?.secondaryKeys || []),
    [dualAxis]
  );

  const traces = useMemo(() => {
    const catX = (r) => {
      const ms = r.dateMs;
      return msToCategoryLabel.get(ms) ?? String(r.dateLabel ?? "");
    };
    const sortedRows = [...(rows || [])].sort((a, b) => a.dateMs - b.dateMs);
    return (seriesDefs || []).map((def, idx) => {
      const onSecondary = secondaryKeySet.has(def.key);
      return {
        type: "scatter",
        mode: "lines+markers",
        name: def.label,
        x: sortedRows.map(catX),
        y: sortedRows.map((r) => {
          const val = r[def.key];
          return val == null ? null : Number(val);
        }),
        ...(onSecondary ? { yaxis: "y2" } : {}),
        line: {
          color: def.color || `hsl(${(idx * 47) % 360}, 70%, 40%)`,
          width: 2,
        },
        marker: {
          color: def.color || `hsl(${(idx * 47) % 360}, 70%, 40%)`,
          size: 7,
        },
        connectgaps: false,
      };
    });
  }, [rows, seriesDefs, msToCategoryLabel, secondaryKeySet]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: POND_STAT_MOBILE_HEIGHT,
    desktopDefault: POND_STAT_DESKTOP_HEIGHT,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const primaryKeys = useMemo(
    () => new Set(dualAxis?.primaryKeys || []),
    [dualAxis]
  );

  const yVals = useMemo(() => {
    const v = [];
    if (dualAxis) {
      const sortedRows = [...(rows || [])].sort((a, b) => a.dateMs - b.dateMs);
      (seriesDefs || []).forEach((def) => {
        if (!primaryKeys.has(def.key)) return;
        sortedRows.forEach((r) => {
          const val = r[def.key];
          if (val != null && Number.isFinite(Number(val))) v.push(Number(val));
        });
      });
    } else {
      traces.forEach((t) => {
        (t.y || []).forEach((y) => {
          if (y != null && Number.isFinite(y)) v.push(y);
        });
      });
    }
    return v;
  }, [traces, rows, seriesDefs, dualAxis, primaryKeys]);

  const yRange = useMemo(() => {
    if (!yVals.length) return { yMin: 0, yMax: 1 };
    let lo = Math.min(...yVals);
    let hi = Math.max(...yVals);
    if (lo === hi) {
      lo -= 1;
      hi += 1;
    }
    const pad = (hi - lo) * 0.08;
    const yMin = dualAxis ? Math.max(0, lo - pad) : lo - pad;
    return { yMin, yMax: hi + pad };
  }, [yVals, dualAxis]);

  const dualAxisTicks = useMemo(() => {
    if (!dualAxis) return null;
    const lo = yVals.length ? Math.min(...yVals) : 0;
    const hi = yVals.length ? Math.max(...yVals) : 1;
    return buildWqDualAxisTicks(lo, hi, dualAxis.secondaryRange || [0, 10], {
      secondaryStep: dualAxis.secondaryStep ?? 1,
      tickCount: dualAxis.tickCount ?? 10,
    });
  }, [dualAxis, yVals]);

  if (!rows || rows.length === 0 || !seriesDefs || seriesDefs.length === 0) {
    return (
      <p className="text-muted text-center py-6 mb-0">No water quality data for the selected sessions.</p>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GraphV2FullscreenInlineRow />
        <Plot
          ref={plotRef}
          data={traces}
          layout={{
            autosize: true,
            height: plotHeight,
            margin: {
              t: legendLayout.marginTop,
              r: dualAxis ? 80 : 40,
              b: xAxisTickLayout.marginBottom,
              l: 72,
            },
            paper_bgcolor: "white",
            plot_bgcolor: "white",
            title: { text: "" },
            xaxis: {
              type: "category",
              categoryorder: "array",
              categoryarray,
              title: plotlyAxisTitle("Date", isMobile),
              ...(xAxisTickLayout.tickvals && xAxisTickLayout.tickvals.length
                ? {
                    tickmode: "array",
                    tickvals: xAxisTickLayout.tickvals,
                    ticktext: xAxisTickLayout.ticktext,
                  }
                : {}),
              tickangle: xAxisTickLayout.tickangle,
              automargin: true,
              ...plotlyAxisBase(isMobile, "default"),
              tickfont: {
                ...plotlyAxisBase(isMobile, "default").tickfont,
                size: xAxisTickLayout.tickFontSize,
              },
            },
            yaxis: dualAxisTicks
              ? {
                  title: plotlyAxisTitle(
                    dualAxis.primaryTitle || "Value",
                    isMobile
                  ),
                  tickmode: "array",
                  tickvals: dualAxisTicks.primaryTicks,
                  ticktext: dualAxisTicks.primaryTickText,
                  range: [dualAxisTicks.primaryMin, dualAxisTicks.primaryMax],
                  ...plotlyAxisBase(isMobile, "default"),
                  showgrid: true,
                }
              : {
                  title: plotlyAxisTitle(
                    dualAxis?.primaryTitle || yAxisTitle || "mg/L",
                    isMobile
                  ),
                  range: [yRange.yMin, yRange.yMax],
                  ...plotlyAxisBase(isMobile, "default"),
                },
            ...(dualAxisTicks
              ? {
                  yaxis2: {
                    title: plotlyAxisTitle(
                      dualAxis.secondaryTitle || "mg/L",
                      isMobile
                    ),
                    overlaying: "y",
                    side: "right",
                    tickmode: "array",
                    tickvals: dualAxisTicks.secondaryTicks,
                    range: [
                      dualAxisTicks.secondaryMin,
                      dualAxisTicks.secondaryMax,
                    ],
                    ...plotlyAxisBase(isMobile, "default"),
                    showgrid: false,
                    zeroline: false,
                  },
                }
              : {}),
            legend: legendLayout.legend,
            hovermode: "closest",
          }}
          config={{ responsive: true, displaylogo: false }}
          style={{ width: "100%" }}
          useResizeHandler
        />
    </div>
  );
}
